import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Typography,
  Space,
  Tag,
  Button,
  Select,
  Statistic,
  Progress,
  Alert,
  Modal,
  Tabs,
  List,
  Badge,
  message,
  Divider
} from 'antd';
import {
  ApiOutlined,
  MessageOutlined,
  SendOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  ReloadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import {
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import moment from 'moment';

const { Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const APIUsageAnalytics = ({ visible, onClose, filterType: _filterType }) => {
  const [_loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState('7days');
  const [_dateRange, _setDateRange] = useState([moment().subtract(7, 'days'), moment()]);

  // API Usage Data
  const [apiData, _setApiData] = useState({
    totalApiCalls: 2456789,
    dailyApiCalls: 78901,
    successRate: 99.2,
    errorRate: 0.8,
    averageResponseTime: 245,
    peakUsageTime: '2:00 PM - 3:00 PM',
    totalMessagesProcessed: 1789456,
    messagesThisMonth: 456123,
    messagesToday: 15678,
    activeWebhooks: 156,
    
    trends: {
      apiCalls: { change: 18.9, direction: 'up' },
      messages: { change: 15.2, direction: 'up' },
      successRate: { change: 0.3, direction: 'up' },
      responseTime: { change: -8.5, direction: 'down' }
    }
  });

  // API Endpoint Usage Data
  const endpointData = [
    {
      endpoint: '/send-message',
      totalCalls: 456789,
      dailyCalls: 15678,
      successRate: 99.2,
      avgResponseTime: 245,
      errors: 3654,
      lastUsed: '2024-03-15 14:35:22',
      primaryUsers: ['TechCorp Ltd', 'HealthcarePro', 'RetailChain Plus'],
      usage: 'High'
    },
    {
      endpoint: '/get-contacts',
      totalCalls: 234567,
      dailyCalls: 8901,
      successRate: 99.8,
      avgResponseTime: 123,
      errors: 468,
      lastUsed: '2024-03-15 14:34:15',
      primaryUsers: ['StartupXYZ', 'EduTech Solutions'],
      usage: 'Medium'
    },
    {
      endpoint: '/templates',
      totalCalls: 123456,
      dailyCalls: 4567,
      successRate: 99.5,
      avgResponseTime: 189,
      errors: 617,
      lastUsed: '2024-03-15 14:32:08',
      primaryUsers: ['Digital Solutions Inc', 'Marketing Pro'],
      usage: 'Medium'
    },
    {
      endpoint: '/upload-media',
      totalCalls: 89012,
      dailyCalls: 3456,
      successRate: 98.9,
      avgResponseTime: 567,
      errors: 979,
      lastUsed: '2024-03-15 14:30:45',
      primaryUsers: ['Global Comm Partners'],
      usage: 'Low'
    },
    {
      endpoint: '/webhooks',
      totalCalls: 67890,
      dailyCalls: 2345,
      successRate: 99.7,
      avgResponseTime: 98,
      errors: 204,
      lastUsed: '2024-03-15 14:33:12',
      primaryUsers: ['TechConnect Solutions'],
      usage: 'Low'
    }
  ];

  // Hourly API Usage Data
  const hourlyUsageData = [
    { hour: '00:00', calls: 1234, messages: 456, errors: 12 },
    { hour: '02:00', calls: 2345, messages: 789, errors: 15 },
    { hour: '04:00', calls: 1876, messages: 567, errors: 8 },
    { hour: '06:00', calls: 3456, messages: 1234, errors: 23 },
    { hour: '08:00', calls: 8901, messages: 3456, errors: 45 },
    { hour: '10:00', calls: 12345, messages: 5678, errors: 67 },
    { hour: '12:00', calls: 15678, messages: 7890, errors: 89 },
    { hour: '14:00', calls: 18901, messages: 9012, errors: 101 },
    { hour: '16:00', calls: 16789, messages: 8234, errors: 95 },
    { hour: '18:00', calls: 13456, messages: 6789, errors: 78 },
    { hour: '20:00', calls: 9876, messages: 4567, errors: 56 },
    { hour: '22:00', calls: 5432, messages: 2345, errors: 34 }
  ];

  // Message Type Distribution
  const messageTypeData = [
    { type: 'Text Messages', count: 789456, percentage: 52.3, color: '#1890ff' },
    { type: 'Template Messages', count: 456123, percentage: 30.2, color: '#52c41a' },
    { type: 'Media Messages', count: 234567, percentage: 15.5, color: '#722ed1' },
    { type: 'Interactive Messages', count: 89012, percentage: 5.9, color: '#fa8c16' },
    { type: 'Document Messages', count: 34567, percentage: 2.3, color: '#13c2c2' }
  ];

  // Error Analysis Data
  const errorData = [
    { type: 'Rate Limit Exceeded', count: 1234, percentage: 45.2, severity: 'High', color: '#ff4d4f' },
    { type: 'Invalid Phone Number', count: 876, percentage: 32.1, severity: 'Medium', color: '#faad14' },
    { type: 'Template Not Found', count: 456, percentage: 16.7, severity: 'Medium', color: '#fa8c16' },
    { type: 'Authentication Failed', count: 234, percentage: 8.6, severity: 'High', color: '#ff7875' },
    { type: 'Network Timeout', count: 123, percentage: 4.5, severity: 'Low', color: '#ffadd6' }
  ];

  // Customer Usage Ranking
  const customerUsageData = [
    { id: 1, customer: 'TechCorp Ltd', calls: 89456, messages: 67890, plan: 'Enterprise', usage: 'Very High' },
    { id: 2, customer: 'HealthcarePro', calls: 67890, messages: 52345, plan: 'Enterprise', usage: 'High' },
    { id: 3, customer: 'RetailChain Plus', calls: 54321, messages: 43210, plan: 'Professional', usage: 'High' },
    { id: 4, customer: 'StartupXYZ', calls: 43210, messages: 34567, plan: 'Business', usage: 'Medium' },
    { id: 5, customer: 'EduTech Solutions', calls: 32109, messages: 25678, plan: 'Business', usage: 'Medium' }
  ];

  useEffect(() => {
    fetchAPIData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange]);

  const fetchAPIData = async () => {
    setLoading(true);
    try {
      // Replace with actual API call
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Error fetching API data:', error);
      message.error('Failed to fetch API usage data');
      setLoading(false);
    }
  };

  const getUsageColor = (usage) => {
    switch (usage.toLowerCase()) {
      case 'very high': return '#ff4d4f';
      case 'high': return '#fa8c16';
      case 'medium': return '#faad14';
      case 'low': return '#52c41a';
      default: return '#d9d9d9';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity.toLowerCase()) {
      case 'high': return '#ff4d4f';
      case 'medium': return '#faad14';
      case 'low': return '#52c41a';
      default: return '#d9d9d9';
    }
  };

  const renderTrend = (trend) => {
    const Icon = trend.direction === 'up' ? ArrowUpOutlined : ArrowDownOutlined;
    const color = trend.direction === 'up' ? '#52c41a' : '#ff4d4f';
    
    return (
      <Space style={{ color }}>
        <Icon />
        <Text style={{ color, fontSize: '12px' }}>{Math.abs(trend.change)}%</Text>
      </Space>
    );
  };

  const endpointColumns = [
    {
      title: 'Endpoint',
      dataIndex: 'endpoint',
      key: 'endpoint',
      render: (endpoint) => (
        <Space>
          <ApiOutlined style={{ color: '#1890ff' }} />
          <Text code style={{ fontSize: '12px' }}>{endpoint}</Text>
        </Space>
      )
    },
    {
      title: 'Usage Level',
      dataIndex: 'usage',
      key: 'usage',
      render: (usage) => (
        <Tag color={getUsageColor(usage)}>{usage}</Tag>
      )
    },
    {
      title: 'Total Calls',
      dataIndex: 'totalCalls',
      key: 'totalCalls',
      render: (calls) => calls.toLocaleString(),
      sorter: (a, b) => a.totalCalls - b.totalCalls
    },
    {
      title: 'Daily Calls',
      dataIndex: 'dailyCalls',
      key: 'dailyCalls',
      render: (calls) => calls.toLocaleString(),
      sorter: (a, b) => a.dailyCalls - b.dailyCalls
    },
    {
      title: 'Success Rate',
      dataIndex: 'successRate',
      key: 'successRate',
      render: (rate) => (
        <Space>
          <Progress
            percent={rate}
            size="small"
            strokeColor={rate >= 99 ? '#52c41a' : rate >= 95 ? '#faad14' : '#ff4d4f'}
            style={{ width: 60 }}
          />
          <Text style={{ fontSize: '12px' }}>{rate}%</Text>
        </Space>
      )
    },
    {
      title: 'Avg Response Time',
      dataIndex: 'avgResponseTime',
      key: 'avgResponseTime',
      render: (time) => `${time}ms`
    },
    {
      title: 'Errors',
      dataIndex: 'errors',
      key: 'errors',
      render: (errors) => (
        <Text style={{ color: errors > 1000 ? '#ff4d4f' : '#666' }}>
          {errors.toLocaleString()}
        </Text>
      )
    },
    {
      title: 'Primary Users',
      dataIndex: 'primaryUsers',
      key: 'primaryUsers',
      render: (users) => (
        <div>
          {users.slice(0, 2).map((user, index) => (
            <Tag key={index} size="small" style={{ fontSize: '10px', marginBottom: 2 }}>
              {user}
            </Tag>
          ))}
          {users.length > 2 && (
            <Text style={{ fontSize: '10px', color: '#666' }}>
              +{users.length - 2} more
            </Text>
          )}
        </div>
      )
    }
  ];

  const customerUsageColumns = [
    {
      title: 'Rank',
      key: 'rank',
      render: (_, __, index) => (
        <Badge
          count={index + 1}
          style={{
            backgroundColor: index < 3 ? '#faad14' : '#d9d9d9',
            color: index < 3 ? '#fff' : '#666'
          }}
        />
      )
    },
    {
      title: 'Customer',
      dataIndex: 'customer',
      key: 'customer',
      render: (customer, record) => (
        <div>
          <Text strong>{customer}</Text>
          <br />
          <Tag color={record.plan === 'Enterprise' ? 'purple' : 
                     record.plan === 'Professional' ? 'blue' : 'green'}
               size="small">
            {record.plan}
          </Tag>
        </div>
      )
    },
    {
      title: 'API Calls',
      dataIndex: 'calls',
      key: 'calls',
      render: (calls) => calls.toLocaleString(),
      sorter: (a, b) => a.calls - b.calls
    },
    {
      title: 'Messages Sent',
      dataIndex: 'messages',
      key: 'messages',
      render: (messages) => messages.toLocaleString(),
      sorter: (a, b) => a.messages - b.messages
    },
    {
      title: 'Usage Level',
      dataIndex: 'usage',
      key: 'usage',
      render: (usage) => (
        <Tag color={getUsageColor(usage)}>{usage}</Tag>
      )
    },
    {
      title: 'Call to Message Ratio',
      key: 'ratio',
      render: (_, record) => (
        <Text>{(record.calls / record.messages).toFixed(2)}</Text>
      )
    }
  ];

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Space>
            <ApiOutlined style={{ color: '#1890ff' }} />
            <span>WhatsApp API Usage Analytics</span>
          </Space>
          <Space>
            <Select
              value={timeRange}
              onChange={setTimeRange}
              style={{ width: 120 }}
              size="small"
            >
              <Option value="24hours">24 Hours</Option>
              <Option value="7days">7 Days</Option>
              <Option value="30days">30 Days</Option>
              <Option value="custom">Custom</Option>
            </Select>
            <Button icon={<ReloadOutlined />} onClick={fetchAPIData} size="small">
              Refresh
            </Button>
            <Button icon={<DownloadOutlined />} size="small">
              Export
            </Button>
          </Space>
        </div>
      }
      visible={visible}
      onCancel={onClose}
      width={1600}
      footer={null}
      style={{ top: 20 }}
    >
      <Tabs defaultActiveKey="overview" type="card">
        <TabPane tab="API Overview" key="overview">
          {/* Key Metrics */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total API Calls"
                  value={apiData.totalApiCalls}
                  prefix={<ApiOutlined style={{ color: '#1890ff' }} />}
                  suffix={renderTrend(apiData.trends.apiCalls)}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Messages Processed"
                  value={apiData.totalMessagesProcessed}
                  prefix={<MessageOutlined style={{ color: '#52c41a' }} />}
                  suffix={renderTrend(apiData.trends.messages)}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Success Rate"
                  value={apiData.successRate}
                  prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                  suffix={<>{renderTrend(apiData.trends.successRate)} %</>}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Avg Response Time"
                  value={apiData.averageResponseTime}
                  prefix={<ClockCircleOutlined style={{ color: '#722ed1' }} />}
                  suffix={<>{renderTrend(apiData.trends.responseTime)} ms</>}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Hourly Usage Chart */}
          <Card title="API Usage Throughout the Day" style={{ marginBottom: 24 }}>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={hourlyUsageData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <RechartsTooltip />
                <Legend />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="calls"
                  stroke="#1890ff"
                  fill="#1890ff"
                  fillOpacity={0.6}
                  name="API Calls"
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="messages"
                  stroke="#52c41a"
                  strokeWidth={3}
                  name="Messages"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="errors"
                  stroke="#ff4d4f"
                  strokeWidth={2}
                  name="Errors"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>

          {/* Message Types and Error Analysis */}
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card title="Message Type Distribution">
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={messageTypeData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="count"
                      label={({ type, percentage }) => `${type} (${percentage}%)`}
                    >
                      {messageTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Error Analysis">
                <List
                  dataSource={errorData}
                  renderItem={(error) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={
                          <Badge
                            color={error.color}
                            style={{ marginRight: 8 }}
                          />
                        }
                        title={
                          <Space>
                            <Text>{error.type}</Text>
                            <Tag color={getSeverityColor(error.severity)} size="small">
                              {error.severity}
                            </Tag>
                          </Space>
                        }
                        description={
                          <Space>
                            <Text>{error.count.toLocaleString()} occurrences</Text>
                            <Text type="secondary">({error.percentage}%)</Text>
                          </Space>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane tab="Endpoint Analytics" key="endpoints">
          {/* Endpoint Usage Table */}
          <Card title="API Endpoint Usage Details" style={{ marginBottom: 24 }}>
            <Table
              columns={endpointColumns}
              dataSource={endpointData}
              pagination={false}
              size="small"
              rowKey="endpoint"
            />
          </Card>

          {/* Alerts */}
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Alert
                message="High Usage Alert"
                description="The /send-message endpoint has processed 15,678 calls today, approaching daily limits."
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            </Col>
            <Col xs={24} md={12}>
              <Alert
                message="Performance Notice"
                description="Average response time for /upload-media is 567ms, consider optimization."
                type="info"
                showIcon
              />
            </Col>
          </Row>
        </TabPane>

        <TabPane tab="Customer Usage" key="customers">
          {/* Top API Users */}
          <Card title="Top API Users" style={{ marginBottom: 24 }}>
            <Table
              columns={customerUsageColumns}
              dataSource={customerUsageData}
              pagination={false}
              size="small"
              rowKey="id"
            />
          </Card>

          {/* Usage Insights */}
          <Card title="Usage Insights">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <Statistic
                  title="Peak Usage Hour"
                  value={apiData.peakUsageTime}
                  prefix={<ClockCircleOutlined />}
                />
              </Col>
              <Col xs={24} md={8}>
                <Statistic
                  title="Active Webhooks"
                  value={apiData.activeWebhooks}
                  prefix={<GlobalOutlined />}
                />
              </Col>
              <Col xs={24} md={8}>
                <Statistic
                  title="Daily Messages"
                  value={apiData.messagesToday}
                  prefix={<SendOutlined />}
                />
              </Col>
            </Row>
            
            <Divider />
            
            <Alert
              message="Usage Optimization Recommendations"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>Consider implementing rate limiting for high-volume customers</li>
                  <li>Optimize media upload endpoint for better performance</li>
                  <li>Monitor peak hours (2-3 PM) for potential scaling needs</li>
                  <li>Review error patterns to improve API reliability</li>
                </ul>
              }
              type="info"
              showIcon
            />
          </Card>
        </TabPane>
      </Tabs>
    </Modal>
  );
};

export default APIUsageAnalytics;
